import { useMemo } from 'react';
import { useHash } from '../../useHash';

/**
 * Custom hook to get the current modal key from the URL hash.
 * This hook takes a record of modal keys and returns the current modal key based on the URL hash.
 *
 * @template T - The type of the modal keys record.
 * @param {T} modalKeys - A record of modal keys where the key is a string and the value is a string.
 * @returns {string | undefined} - The current modal key if found, otherwise undefined.
 *
 * Example usage:
 * const modalKeys = { modal1: 'modal1Key', modal2: 'modal2Key' };
 * const currentModal = useCurrentModal(modalKeys);
 */
export function useCurrentModal<T extends Record<string, string>>(
  modalKeys: T,
): string | undefined {
  // Memoize the array of modal keys' values
  const modals: string[] = useMemo(() => Object.values(modalKeys), [modalKeys]);

  // Get the current URL hash
  const modalUrl: string = useHash();

  // Memoize the current modal key by finding it in the array of modal keys' values
  const modal: string | undefined = useMemo(
    () => modals.find(modal => modal == modalUrl?.replaceAll('#', '')),
    [modalUrl, modals],
  );

  // Return the current modal key if found, otherwise undefined
  return modal;
}
